import React, { useEffect, useRef }  from "react";
import { Chapter } from "../../compilers/chapter";
import { Course } from "../../compilers/course";
import InfoSectionParent from "../../components/article/InfoSectionParent";
import CourseInfo from "./CourseInfo";
import YellowDivider from "../../components/dividers/YellowDivider";
import Button from "../../components/singlecomponents/Button";
import ShareButtons from "../../components/singlecomponents/ShareButtons";
import { useTranslation } from "react-i18next";
import Modal from '../singlecomponents/Modal'
import { ReactComponent as ChevronRight } from "../../assets/chevron-right.svg";
import { ReactComponent as ProgressCircle } from '../../assets/progress-circle.svg'

type CourseChapter = {
  chapter: Chapter;
  course: Course;
  currentChapterIndex: number;
  setCurrentChapter: (index: number) => void;
  returnToSummary: () => void;
};

const CourseChapter: React.FC<CourseChapter> = ({ chapter, course, setCurrentChapter, currentChapterIndex, returnToSummary }) => {
  const { t, i18n } = useTranslation();
  const mediaArticle = useRef<HTMLDivElement>(null);
  const loadingCircle = useRef<SVGSVGElement>(null);
  const currentIndex = currentChapterIndex;
  const allIndex = course.data.body.length;

  useEffect(() => {
    // 1. TABLE
    /**
    * This function is targeting the media article for creating specific logic for <TABLE>
    * Logic - Get all the <table> tag within the article element and then wrap each of the table with table-wrapper
    * CSS - Using CSS to target to this table-wrapper to style it as overflow: scroll;
    */
    if(mediaArticle?.current) {
      const mediaTables = Array.from(mediaArticle.current?.getElementsByTagName("table"));
      mediaTables.forEach(tag => tag.outerHTML = `<div class="table-wrapper">${tag.outerHTML}</div>`)
    }
    // 2. ANCHOR LINK
    const allLinks = Array.from(mediaArticle.current?.getElementsByTagName("a"));
    allLinks.forEach(link => {
      link.setAttribute("target", "_blank"); 
      if(!link.getAttribute("href").includes("http")){
        link.setAttribute("href", `http://${link.getAttribute("href")}`)
      }
    });
    // 3. PROGRESSING CIRCLE
    /**
     * This function is to target to the BLACK SVG circle within the progress-circle svg to track user current chapter
     * The function included:
     * 1. CurrentProgress: the percentage value to check the current chapter
     * 2. Circle SVG element: target to the required black circle
     * 3. Radius: the radius value within the SVG
     * 4. Circumferece: adjustable style value to fit the black circle on top of the gray circle
     * 5. SetProgress function: Set the current radius to the correct currentProgress percentage value
     * */ 
    const currentProgress = (currentIndex / allIndex) * 100;
    const circle:SVGCircleElement = document.querySelector('#outer-circle');
    const radius = circle.r.baseVal.value;
    const circumference = radius * 2 * Math.PI;

    circle.style.strokeDasharray = `${circumference} ${circumference}`;
    circle.style.strokeDashoffset = `${circumference}`;

    function setProgress(percent:number) {
      const offset = circumference - percent / 100 * circumference;
      circle.style.strokeDashoffset = `${offset}`;
    }
    setProgress(currentProgress);
    
    // 4. ORDERED LIST
    const AllOrderedList = Array.from((mediaArticle.current?.getElementsByTagName("ol")))
    AllOrderedList.forEach(list => {
      const AllList = Array.from(list.children);
      AllList.forEach(li => {
        const LiContent = Array.from(li.children);
        LiContent.forEach(content => {            
          if(content.getElementsByTagName("strong").length > 0 || content.tagName == "STRONG"){
            li.classList.add("bold");
          }
        })
      })
    })
  })

  return (
    <>
      <div className="hidden-desktop mb-3 article-title-container">
        <h1 className="h2">{course.data.title}</h1>
        <YellowDivider heading="h1" />
      </div>
      <div className="media-inner-info hidden-desktop mb-3">
        <p className="article-blurb">{course.data.blurb}</p>
        <div className="info-divider"/>
        <div data-bs-toggle="modal" data-bs-target="#chapterModulesBackdrop" className="progress-circle-wrapper">
          <ProgressCircle ref={loadingCircle} className="progress-circle"/>
          <span className="cta mx-2 d-flex align-items-center">
          {t("inner-page.all-chapters")} &nbsp;
            <ChevronRight
              style={
                i18n.language == "ar"
                  ? { transform: `rotate(180deg)` }
                  : { transform: `rotate(0deg)` }
              }
              className="chevron"
            />
          </span>
        </div>
        <div className="media-inner-share course-inner my-2">
          <InfoSectionParent label={t("inner-page.share")}></InfoSectionParent>
          <ShareButtons/>
        </div>
        <div className="back-btn" onClick={returnToSummary}>
          <Button bootstrapClass="px-sm-2 px-md-4 py-1" name={t("button.back-course")} />
        </div>
        <Modal targetElementName="chapterModulesBackdrop">
          <CourseInfo
            course={course}
            setCurrentChapter={setCurrentChapter}
            currentChapterIndex={currentChapterIndex}
          />
        </Modal>
      </div>
      <h2 className="h3 px-lg-4 pb-3">{chapter.title}</h2>
      <div
        ref={mediaArticle}
        className="media-article px-lg-4"
        dangerouslySetInnerHTML={{ __html: chapter.body }}
      />
    </>
  );
};

export default CourseChapter;
